/**
 * Pushing one layout onto the whole room.
 *
 * "Sync layout to peers" has existed in the Adjust view dialog as a switch and
 * nothing else. `layoutSyncEnabled` was declared in `useMeetingState`, toggled
 * by an action nobody dispatched, and read nowhere; there was no send, no
 * receive and no transport. The switch beside the label was wired to
 * `hideTilesWithoutVideo` — a copy of the row above it — so the one thing it did
 * was change a different setting.
 *
 * This is the decision half: what may be sent, and what may be believed. The
 * React wiring is in `useLayoutSync`.
 *
 * ## Why the envelope looks like the stage one
 *
 * It rides the generic `broadcast` event rather than an event of its own, and
 * that is not a stylistic choice. The room channel has no catch-all clause, so
 * an event an older signalling server does not implement raises
 * FunctionClauseError and takes the channel process — and the sender's place in
 * the room — down with it. `useStageContent` learned this already; the same
 * reasoning applies to every new client-to-client message.
 *
 * ## Why an unknown mode is dropped rather than applied
 *
 * The layout mode indexes the grid's rendering branches. A value from outside
 * the set — an older or newer client, a hand-crafted payload — would land in no
 * branch and leave the receiver looking at nothing, with no way back except a
 * reload. A broadcast that cannot be understood is ignored, which leaves the
 * viewer where they were.
 */

import type { LayoutMode } from '../store/meeting-layout.store'

export type { LayoutMode }

/**
 * Every layout that may travel between clients.
 *
 * Listed rather than derived because a runtime guard needs actual values, and
 * kept honest by the check below: `dual-stream` was missed on the first pass,
 * which would have left a moderator using it unable to sync it and any client
 * receiving it silently ignoring the push. A layout added to the store without
 * being added here is now a compile error rather than a mode that quietly
 * refuses to synchronise.
 */
export const LAYOUT_MODES = [
  'auto',
  'tiled',
  'spotlight',
  'sidebar',
  'pinned',
  'dual-stream',
] as const satisfies readonly LayoutMode[]

/** Fails to compile if the store gains a mode this list does not carry. */
type MissingFromLayoutModes = Exclude<LayoutMode, (typeof LAYOUT_MODES)[number]>
const _everyLayoutModeIsSyncable: MissingFromLayoutModes extends never ? true : never = true
void _everyLayoutModeIsSyncable

/** The `body` of the generic broadcast this feature uses. */
export type LayoutSyncEnvelope = {
  __inmeet?: unknown
  action?: unknown
  mode?: unknown
  by?: unknown
}

export function isLayoutMode(value: unknown): value is LayoutMode {
  return typeof value === 'string' && (LAYOUT_MODES as readonly string[]).includes(value)
}

/**
 * The layout an incoming broadcast asks for, or null to ignore it.
 *
 * Own messages are dropped. Every client in the room receives its own
 * broadcasts, and a moderator applying their own push would be harmless but
 * would also re-enter the layout they are already in on every change — enough
 * to fight a selection they are in the middle of making.
 */
export function readLayoutSync(
  payload: unknown,
  localPeerId: string | null | undefined,
): LayoutMode | null {
  const env = ((payload as { body?: LayoutSyncEnvelope })?.body ?? {}) as LayoutSyncEnvelope
  if (env.__inmeet !== 'layout' || env.action !== 'sync') return null
  if (env.by && localPeerId && env.by === localPeerId) return null
  return isLayoutMode(env.mode) ? env.mode : null
}

/** The body to broadcast. Separate from the decision so the shape is testable. */
export function layoutSyncBody(mode: LayoutMode, by: string | null | undefined) {
  return {
    __inmeet: 'layout',
    action: 'sync',
    mode,
    ...(by ? { by } : {}),
  }
}

export type BroadcastInput = {
  /** Only a host or co-host may move everybody else's screen. */
  isModerator: boolean
  /** The switch in the Adjust view dialog. */
  enabled: boolean
  /** The layout this client is in now. */
  mode: LayoutMode
  /** What was last pushed, or null if nothing has been. */
  lastSent: LayoutMode | null
}

/**
 * Should this client push its layout to the room?
 *
 * The moderator check is repeated here even though the dialog only offers the
 * switch to moderators. A switch that is merely hidden is not a permission, and
 * the flag survives in state after a demotion — so a moderator who is demoted
 * mid-meeting would otherwise carry on moving everyone's screen.
 *
 * Unchanged layouts are not re-sent. The caller runs on every layout render, and
 * without this the room would be told the same thing continuously, which costs
 * a broadcast per render and re-snaps anyone who had deliberately moved away.
 */
export function shouldBroadcastLayout({
  isModerator,
  enabled,
  mode,
  lastSent,
}: BroadcastInput): boolean {
  if (!isModerator || !enabled) return false
  return mode !== lastSent
}
